import React, { useState } from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  SimpleGrid, 
  Input, 
  InputGroup, 
  InputLeftElement,
  Stack,
  Select,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  Badge,
  useColorModeValue,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from 'react-query';

import { getPackages, searchPackages, type Package, type SearchParams } from '../services/packageService';

const PackageCard = ({ pkg }: { pkg: Package }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Card 
      as={RouterLink} 
      to={`/packages/${pkg.id}`}
      bg={cardBg}
      transition="all 0.3s"
      _hover={{ 
        transform: 'translateY(-5px)', 
        boxShadow: 'md',
        bg: hoverBg
      }}
      height="100%"
      borderRadius="lg"
    >
      <CardBody>
        <Stack spacing={3}>
          <Flex justify="space-between" align="flex-start">
            <Heading size="md" color="brand.500">{pkg.name}</Heading>
            <Badge colorScheme="blue" fontSize="0.8em" borderRadius="full" px="2">
              v{pkg.latest_version}
            </Badge>
          </Flex>
          <Text color={textColor}>{pkg.description}</Text>
          <Divider />
          <Flex justifyContent="space-between" fontSize="sm" color={textColor}>
            <Text>By: {pkg.author}</Text>
            <Text>{pkg.downloads.toLocaleString()} downloads</Text>
          </Flex>
        </Stack>
      </CardBody>
    </Card>
  );
};

export default function PackagesPage() {
  const [searchParams, setSearchParams] = useState<SearchParams>({
    query: '',
    skip: 0,
    limit: 12,
    sort: 'downloads',
    order: 'desc'
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Use React Query to fetch packages
  const { data, isLoading, isError, error } = useQuery(
    ['packages', searchParams],
    () => searchParams.query ? searchPackages(searchParams) : getPackages(searchParams.skip, searchParams.limit),
    { keepPreviousData: true }
  );

  const handleSearch = () => {
    setSearchParams({
      ...searchParams,
      query: searchTerm,
      skip: 0 // Reset pagination when searching
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const [sort, order] = value.split('-');
    setSearchParams({
      ...searchParams,
      sort: sort as 'downloads' | 'name' | 'latest_version' | 'created_at',
      order: order as 'asc' | 'desc'
    });
  };

  // Get the packages to display - now both APIs return the same format
  const packages = data?.results || []; // Use empty array as fallback
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const totalCount = data?.total || 0;

  return (
    <Box>
      <Heading mb={6}>MCP Packages</Heading>
      <Text mb={8} fontSize="lg" color="gray.600" _dark={{ color: 'gray.400' }}>
        Discover and install MCP servers from our package registry
      </Text>
      
      {/* Search and filters */}
      <Stack 
        direction={{ base: 'column', md: 'row' }} 
        spacing={4} 
        mb={8}
        align="flex-end"
      >
        <Box flexGrow={1}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input 
              placeholder="Search packages" 
              borderRadius="md" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </InputGroup>
        </Box>
        <Button onClick={handleSearch} colorScheme="brand">Search</Button>
        <Select 
          placeholder="Sort by" 
          width={{ base: '100%', md: '200px' }}
          defaultValue="downloads-desc"
          onChange={handleSortChange}
        >
          <option value="downloads-desc">Most Downloads</option>
          <option value="downloads-asc">Least Downloads</option>
          <option value="name-asc">Name (A-Z)</option>
          <option value="name-desc">Name (Z-A)</option>
          <option value="created_at-desc">Newest</option>
          <option value="created_at-asc">Oldest</option>
        </Select>
      </Stack>

      {/* Loading state */}
      {isLoading && (
        <Center py={10}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
        </Center>
      )}

      {/* Error state */}
      {isError && (
        <Alert status="error" borderRadius="md" mb={8}>
          <AlertIcon />
          <AlertTitle mr={2}>Error loading packages!</AlertTitle>
          <AlertDescription>{(error as Error).message || 'Please try again later.'}</AlertDescription>
        </Alert>
      )}

      {/* No results */}
      {!isLoading && !isError && packages?.length === 0 && (
        <Alert status="info" borderRadius="md" mb={8}>
          <AlertIcon />
          <AlertTitle mr={2}>No packages found!</AlertTitle>
          <AlertDescription>
            {searchParams.query 
              ? `No packages match the search term "${searchParams.query}".` 
              : 'There are no packages available yet.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Package grid */}
      {!isLoading && !isError && packages?.length > 0 && (
        <>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {packages.map((pkg: Package) => (
              <PackageCard key={pkg.id} pkg={pkg} />
            ))}
          </SimpleGrid>

          {/* Pagination - would implement with load more button or pagination component */}
          {/* For simplicity, just showing a Load More button that fetches the next page */}
          {searchParams.query ? (
            // Only show pagination for search results if there are more results
            data && (data as any).total > (searchParams.skip || 0) + (searchParams.limit || 10) && (
              <Center mt={8}>
                <Button 
                  onClick={() => setSearchParams({
                    ...searchParams,
                    skip: (searchParams.skip || 0) + (searchParams.limit || 10)
                  })}
                  colorScheme="brand"
                  variant="outline"
                >
                  Load More
                </Button>
              </Center>
            )
          ) : (
            // For regular listing, always show Load More if there are packages
            packages.length >= (searchParams.limit || 10) && (
              <Center mt={8}>
                <Button 
                  onClick={() => setSearchParams({
                    ...searchParams,
                    skip: (searchParams.skip || 0) + (searchParams.limit || 10)
                  })}
                  colorScheme="brand"
                  variant="outline"
                >
                  Load More
                </Button>
              </Center>
            )
          )}
        </>
      )}
    </Box>
  );
}
